<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores'
import { useTheme } from '@/composables/useTheme'
import NotificationBell from '@/components/NotificationBell.vue'

const router = useRouter()
const userStore = useUserStore()
const { resolved, toggle } = useTheme()

const logoSrc = computed(() =>
  resolved.value === 'dark' ? '/logo-dark.svg' : '/logo.svg'
)

const searchQuery = ref('')

function handleSearch(): void {
  if (searchQuery.value.trim()) {
    router.push({ path: '/apps', query: { q: searchQuery.value } })
  }
}

function handleLogout(): void {
  userStore.logout()
  router.push('/login')
}
</script>

<template>
  <header class="app-header">
    <div class="app-header__brand" @click="router.push('/')">
      <img :src="logoSrc" alt="阿旺应用中心" class="app-header__logo">
      <span class="app-header__title">阿旺应用中心</span>
    </div>

    <div class="app-header__search">
      <input
        v-model="searchQuery"
        class="app-header__search-input"
        placeholder="搜索应用..."
        @keydown.enter="handleSearch"
      >
    </div>

    <div class="app-header__actions">
      <button class="app-header__theme" @click="toggle">
        {{ resolved === 'dark' ? '🌙' : '☀️' }}
      </button>
      <NotificationBell />
      <span v-if="userStore.org" class="app-header__org">
        {{ userStore.org.name }}
      </span>
      <div class="app-header__user">
        <span v-if="userStore.user">{{ userStore.user.displayName }}</span>
        <button class="app-header__logout" @click="handleLogout">退出</button>
      </div>
    </div>
  </header>
</template>

<style scoped lang="scss">
.app-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 var(--aw-space-6);
  background-color: var(--aw-bg-base);
  border-bottom: 1px solid var(--aw-border-default);

  &__brand {
    display: flex;
    align-items: center;
    gap: var(--aw-space-3);
    cursor: pointer;
  }

  &__logo {
    width: 32px;
    height: 32px;
    border-radius: var(--aw-radius-md);
    object-fit: cover;
  }

  &__title {
    font-size: var(--aw-text-md);
    font-weight: var(--aw-font-semibold);
    color: var(--aw-text-primary);
  }

  &__search {
    flex: 1;
    max-width: 480px;
    margin: 0 var(--aw-space-6);
  }

  &__search-input {
    width: 100%;
    height: 36px;
    padding: 0 var(--aw-space-4);
    border: 1px solid var(--aw-border-default);
    border-radius: var(--aw-radius-md);
    background-color: var(--aw-bg-subtle);
    font-size: var(--aw-text-sm);
    color: var(--aw-text-primary);
    outline: none;
    transition: all var(--aw-duration-fast);

    &::placeholder {
      color: var(--aw-text-tertiary);
    }

    &:focus {
      border-color: var(--aw-primary-300);
      box-shadow: var(--aw-shadow-focus);
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: var(--aw-space-4);
  }

  &__org {
    font-size: var(--aw-text-sm);
    color: var(--aw-text-secondary);
    padding: var(--aw-space-1) var(--aw-space-3);
    background-color: var(--aw-bg-muted);
    border-radius: var(--aw-radius-sm);
  }

  &__user {
    display: flex;
    align-items: center;
    gap: var(--aw-space-3);
    font-size: var(--aw-text-sm);
  }

  &__theme {
    padding: var(--aw-space-1) var(--aw-space-2);
    border: none;
    border-radius: var(--aw-radius-sm);
    background-color: transparent;
    font-size: var(--aw-text-md);
    cursor: pointer;
    transition: all var(--aw-duration-fast);

    &:hover {
      background-color: var(--aw-bg-muted);
    }
  }

  &__logout {
    padding: var(--aw-space-1) var(--aw-space-3);
    border: none;
    border-radius: var(--aw-radius-sm);
    background-color: transparent;
    color: var(--aw-text-secondary);
    font-size: var(--aw-text-sm);
    cursor: pointer;
    transition: all var(--aw-duration-fast);

    &:hover {
      background-color: var(--aw-bg-muted);
      color: var(--aw-danger-500);
    }
  }
}
</style>
